const PROVENANCE_LABELS: Record<string, string> = {
  misumi_standard: 'MISUMI standard',
  partner: 'Partner',
  fictiv_custom: 'Fictiv custom',
}

/**
 * Human-readable label for `Sourceable.provenance` (UID-32), shared by every
 * catalog result rendering (`ResultCard.tsx`) so the views never drift. Falls
 * back to the raw value for a provenance this map hasn't been taught yet,
 * rather than hiding it.
 */
export function provenanceLabel(provenance: string | null | undefined): string | null {
  if (!provenance) return null
  return PROVENANCE_LABELS[provenance] ?? provenance
}

/** The card's display name — same "name, then SKU, then a neutral fallback"
 * order every catalog result rendering uses. */
export function resultDisplayName(sourceable: {
  readonly name?: string | null
  readonly catalogSku?: string | null
}): string {
  return sourceable.name ?? sourceable.catalogSku ?? 'Untitled'
}

/**
 * The product-card SOURCE BADGE pill text (UID-253), overlaid top-left on
 * the result thumbnail. Judgment call: the design's only two badge values
 * are "MISUMI" and "Fictiv" (README §7.3 — "MISUMI catalog + your Fictiv
 * parts"), but the wire's `provenance` has THREE values (`misumi_standard |
 * partner | fictiv_custom`) and catalog search only ever emits the first two
 * (`fictiv_custom` is the CAD/custom-part origin, unreachable from a catalog
 * row — see result-card-mapper.ts). There is no dedicated "is this a Fictiv
 * part" field on the wire today, so this maps the catalog-reachable
 * `partner` population to the "Fictiv" badge (the storefront's only other
 * source population besides MISUMI-standard rows) rather than surfacing
 * "Partner" — a label the design never shows. `fictiv_custom` is included
 * for completeness/exhaustiveness even though it can't occur here yet. This
 * is DELIBERATELY separate from `provenanceLabel` above (which still renders
 * "Partner" wherever that finer label is wanted) so this presentation-only
 * badge choice never leaks into that shared mapping.
 */
export function sourceBadgeLabel(provenance: string | null | undefined): string {
  return provenance === 'misumi_standard' ? 'MISUMI' : 'Fictiv'
}

/**
 * A short "Lead time" data-point label for a catalog result's
 * `CatalogItemAvailability` — used where lead time needs to stand alone next
 * to price (the "Recently viewed" quick-view modal's two-column Lead
 * time/Price row, `Fictiv Platform.dc.html` ~L4585), distinct from
 * `availability.note`'s combined "status · lead-time" sentence rendered on
 * the card itself (`ResultCard.tsx`). Falls back to the note verbatim when
 * `leadTimeDays` is null (the demo heuristic's own "unknown" case) rather
 * than inventing a range.
 */
export function leadTimeLabel(availability: {
  readonly note: string
  readonly leadTimeDays: { readonly min: number; readonly max: number } | null
}): string {
  if (!availability.leadTimeDays) return availability.note
  const { min, max } = availability.leadTimeDays
  return min === max ? `${min} business days` : `${min}–${max} business days`
}
